import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { zonesTableTabsTableColumns } from 'configs';
import { ROUTES } from 'consts';
import { EZoneStatus } from 'enums';
import { IEnhancedTableHeadCell, IZone, IZoneWithActions } from 'models';
import {
  useFetchZoneQuery,
  useFetchZonesQuery,
  useLazyDownloadDecalQuery,
  usePublicFetchZoneLogQuery,
  usePublicFetchZoneQuery,
} from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';
import { useLoadingToast } from '../../hooks/toasts/loading-toast.hook';

type BuildingId = number | null;

type UseZonesProps = {
  buildingId?: BuildingId;
  includeInactive?: boolean;
};

export const useZones = (props: UseZonesProps) => {
  const { buildingId } = props;
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);

  const { data, isLoading } = useFetchZonesQuery({ orgSlug });

  let zones = (data || []).map((zone: IZone) => ({
    ...zone,
    floorInfo: zone?.floor?.floor || `${zone.building_floor_id}`,
    rating: zone.rating || 0,
    lastServicedBy: (zone.logs || []).length > 0 ? zone.logs[0]?.day_porter?.name || '' : '',
    serviceItemsText: (zone?.service_items || []).map((serviceItem) => serviceItem.name).join(', '),
  }));

  if (buildingId) {
    zones = zones.filter((zone: IZone) => zone.building_id === buildingId);
  }

  const activeZones = zones.filter((zone: any) => zone.status === EZoneStatus.ACTIVE);
  const pendingZones = zones.filter((zone: any) => zone.status === EZoneStatus.PENDING);
  const inactiveZones = zones.filter((zone: any) => zone.status === EZoneStatus.INACTIVE);

  return useMemo(
    () =>
      [
        isLoading,
        {
          all: zones,
          active: activeZones,
          pending: pendingZones,
          inactive: inactiveZones,
        },
      ] as [
        boolean,
        {
          all: IZone[];
          active: IZone[];
          pending: IZone[];
          inactive: IZone[];
        },
      ],
    [isLoading, zones, activeZones, pendingZones, inactiveZones],
  );
};

type UseZoneProps = {
  zoneId: number;
};

export const useZone = ({ zoneId }: UseZoneProps): [boolean, IZone] => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);

  const { data, isLoading } = useFetchZoneQuery({ orgSlug, id: zoneId });

  return useMemo(() => [isLoading, data], [isLoading, data]);
};

type UsePublicZoneProps = {
  code: string;
};

export const usePublicZone = ({ code }: UsePublicZoneProps): [boolean, IZone] => {
  const { data, isLoading } = usePublicFetchZoneQuery({ code });

  return useMemo(() => [isLoading, data], [isLoading, data]);
};

type UseZonesCountProps = UseZonesProps;

export const usePublicZoneLogs = ({ zoneId, limit }: UsePublicZoneLogProps): [boolean, any[]] => {
  const { data, isLoading } = usePublicFetchZoneLogQuery({ zoneId, limit });

  return useMemo(() => [isLoading, data], [isLoading, data]);
};
type UsePublicZoneLogProps = {
  zoneId: number;
  limit: number;
};

export const useZonesCount = ({
  buildingId,
}: UseZonesCountProps): {
  loading: boolean;
  active: number;
  pending: number;
  inactive: number;
} => {
  const [loading, { active: activeZones, pending: pendingZones, inactive: inactiveZones }] = useZones({ buildingId });

  const active = activeZones.length;
  const pending = pendingZones.length;
  const inactive = inactiveZones.length;

  return { loading, active, pending, inactive };
};

type UseZonesTableRowsProps = UseZonesProps & { setState?: (state: any) => void };

export const useZonesTableRows = ({
  buildingId,
  setState = () => {},
}: UseZonesTableRowsProps): [boolean, IZoneWithActions[]] => {
  const navigate = useNavigate();
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [loading, { all: zones }] = useZones({ buildingId });
  const [downloadDecalTrigger] = useLazyDownloadDecalQuery();

  const viewCleancode = (zone: IZone) => window.open(`${process.env.REACT_APP_CLEANCODE_URL}/${zone.code}`);
  const manageServiceItem = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneServiceItemsDialog: true }));
  const viewCleaningHistory = (zone: IZone) =>
    navigate(`/${orgSlug}/${ROUTES.ZONE_CLEANING_HISTORY.replace(':zoneId', `${zone.id}`)}`);
  const editZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneUpdateDialog: true }));
  const deactivateZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneDeactivateDialog: true }));
  const purchaseZone = () => navigate(`/${orgSlug}/${ROUTES.CART}`);
  const deleteZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneDeleteDialog: true }));
  const activateZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneActivateDialog: true }));
  const suspendService = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleSuspendServiceDialog: true }));
  const downloadDecal = async (zone: IZone) => {
    const loadingToast = useLoadingToast();
    try {
      loadingToast.open({ message: 'Downloading Decal' });
      const result = await downloadDecalTrigger({ orgSlug, id: zone.id, format: 'pdf' });
      if (result.isSuccess) {
        const decalPdfBlob = new Blob([result.data], { type: 'application/pdf' });
        const downloadUrl = URL.createObjectURL(decalPdfBlob);

        const downloadLink = document.createElement('a');
        downloadLink.target = '_blank';
        downloadLink.download = `zone_${zone.code}_decal.pdf`;

        // set object URL as the anchor's href
        downloadLink.href = downloadUrl;

        // append the anchor to document body
        document.body.append(downloadLink);

        // fire a click event on the anchor
        downloadLink.click();
      }
    } finally {
      loadingToast.close();
    }
  };

  const rows = zones.map(
    (zone: IZone) =>
      ({
        ...zone,
        viewCleancode,
        manageServiceItem,
        viewCleaningHistory,
        editZone,
        deactivateZone,
        purchaseZone,
        deleteZone,
        activateZone,
        suspendService,
        downloadDecal,
      } as IZoneWithActions),
  );

  return useMemo(() => {
    return [loading, rows];
  }, [loading, rows]);
};

type UseActiveZonesTableRowsProps = UseZonesTableRowsProps;

export const useActiveZonesTableRows = ({
  buildingId,
  setState,
}: UseActiveZonesTableRowsProps): [boolean, string[], IEnhancedTableHeadCell[], IZoneWithActions[]] => {
  const [loading, allRows] = useZonesTableRows({ buildingId, setState });

  const columnKeys = ['code', 'floorInfo', 'name', 'rating', 'serviceItemsText', 'lastServicedBy', 'actions'];

  const columns = zonesTableTabsTableColumns.filter((column) => columnKeys.includes(column.id));

  const rows = allRows.filter((row) => row.status === EZoneStatus.ACTIVE);

  return [loading, columnKeys, columns, rows];
};

type UseInactiveZonesTableRows = UseZonesTableRowsProps;

export const useInactiveZonesTableRows = ({
  buildingId,
  setState,
}: UseInactiveZonesTableRows): [boolean, string[], IEnhancedTableHeadCell[], IZoneWithActions[]] => {
  const [loading, allRows] = useZonesTableRows({ buildingId, setState });

  const columnKeys = ['code', 'floorInfo', 'name', 'rating', 'serviceItemsText', 'lastServicedBy', 'actions'];

  const columns = zonesTableTabsTableColumns.filter((column) => columnKeys.includes(column.id));

  const rows = allRows.filter((row) => row.status === EZoneStatus.INACTIVE);

  return [loading, columnKeys, columns, rows];
};
